'use client';

import {
  Asterisk,
  BracketsCurly,
  CalendarBlank,
  CaretDown,
  CaretLeft,
  CaretRight,
  CircleHalfTilt,
  Hash,
  type IconProps,
  Key,
  ShoppingCart,
  TextAa,
  Truck,
  TShirt,
} from '@phosphor-icons/react';
import React, { useEffect, useMemo, useState } from 'react';

import { cn } from '@skrym/shared/utils/cn';

import { getModelDefinition, type OpenAPISchema } from '@/client/openAPI';

import { convertToJSONParameter, type JsonParameter } from './endpoint';

interface DataModelProps {
  modelName: string;
  description?: string;
  showCodeName?: boolean;
  collapsed?: boolean;
  /**
    @deprecated Caused a large bundle increase
  */
  icon?: string;
}

function DataModel(props: DataModelProps) {
  const { modelName, description, showCodeName, collapsed } = props;

  const [activeParams, setActiveParams] = useState<Set<string>>(new Set());
  const [isOpen, setIsOpen] = useState(!collapsed);

  const [modelData, setModelData] = useState<OpenAPISchema | null>(null);

  useEffect(() => {
    // biome-ignore lint/style/noNonNullAssertion: FIXME: When touching this code, please remove this non-null assertion
    getModelDefinition(modelName!).then(setModelData);
  }, [modelName]);

  const toggleActiveParam = (param: string) => {
    const newActiveParams = new Set(activeParams);
    if (newActiveParams.has(param)) {
      newActiveParams.delete(param);
    } else {
      newActiveParams.add(param);
    }
    setActiveParams(newActiveParams);
  };

  const mappedReqBodyParams: JsonParameter[] = useMemo(() => {
    if (modelData) {
      return convertToJSONParameter(modelData);
    }
    return [];
  }, [modelData]);

  const renderTypeChip = (
    type: 'string' | 'uuid' | 'int' | 'float' | 'number' | 'boolean' | 'date-time' | string,
    array: boolean,
  ) => {
    let Icon: React.ReactNode;
    const Props: IconProps = { weight: 'bold', size: 12, opacity: 0.7 };
    switch (type) {
      case 'string':
        Icon = <TextAa {...Props} />;
        break;
      case 'uuid':
        Icon = <Key {...Props} />;
        break;
      case 'int':
      case 'integer':
      case 'float':
      case 'number':
        Icon = <Hash {...Props} />;
        break;
      case 'boolean':
        Icon = <CircleHalfTilt {...Props} />;
        break;
      case 'object':
        Icon = <BracketsCurly {...Props} />;
        break;
      case 'transportMethod':
        Icon = <Truck {...Props} />;
        break;
      case 'order':
        Icon = <ShoppingCart {...Props} />;
        break;
      case 'date-time':
        Icon = <CalendarBlank {...Props} />;
        break;
      default:
        Icon = <TShirt {...Props} />;
    }
    return (
      <div className="flex items-center bg-neutral-100 text-neutral-500 dark:bg-neutral-800/50 text-xs  font-mono px-2 py-.9 rounded-md">
        {Icon}
        <span className="ml-1">
          {type}
          {array && '[]'}
        </span>
      </div>
    );
  };

  const renderParams = (
    parameters: JsonParameter[] | undefined,
    indentLevel = 0,
    isResponseType = 'false',
  ): React.ReactNode => {
    // return null if parameters is undefined
    if (!parameters) {
      return null;
    }

    return (
      <div
        className={cn(
          indentLevel > 0 && `ml-${2 * indentLevel} my-3 py-3 border-l border-t border-b rounded-l`,
          isResponseType === 'false' && 'dark:border-neutral-800 border-neutral-200',
          isResponseType === 'success' &&
            `dark:border-green-900/40 border-green-600/30  ml-${2 * (indentLevel + 1)}`,
        )}
      >
        {parameters.map((param) => {
          const isOptional: boolean =
            param.description.toLowerCase().includes('optional') || isResponseType !== 'false';
          return (
            <React.Fragment key={param.name}>
              {/** biome-ignore lint/a11y/noStaticElementInteractions: when touching this code, please fix this lint issue or change this comment to explain why it should be kept */}
              <div
                className={`grid grid-cols-4 gap-x-4 items-start ${
                  indentLevel === 0 ? 'mt-3' : 'mt-2'
                }`}
                onClick={() => toggleActiveParam(`${param.name}-${indentLevel}-${isResponseType}`)}
                onKeyDown={() =>
                  toggleActiveParam(`${param.name}-${indentLevel}-${isResponseType}`)
                }
                style={{ marginLeft: `${indentLevel * 10}px` }}
              >
                <div className="col-span-2">
                  <div />
                  <div className="flex items-center">
                    {activeParams.has(`${param.name}-${indentLevel}-${isResponseType}`) ? (
                      <CaretDown
                        size={16}
                        color={param.children && param.children.length > 0 ? 'gray' : 'transparent'}
                      />
                    ) : (
                      <CaretRight
                        size={16}
                        color={param.children && param.children.length > 0 ? 'gray' : 'transparent'}
                      />
                    )}
                    <span
                      key={`${param.name}-${indentLevel}-${isResponseType}`}
                      className="font-mono mr-1 ml-2"
                    >
                      {param.name}
                    </span>{' '}
                    {!isOptional ? <Asterisk size={10} color="#ef4444" weight="bold" /> : null}
                  </div>
                  <div className={'flex flex-start ml-6'}>
                    {renderTypeChip(param.type, param.array ? param.array : false)}
                  </div>
                </div>
                <p className="col-span-2 text-sm mr-3 text-neutral-500 mt-2">
                  {param.description ? param.description : '-'}
                </p>
              </div>
              {activeParams.has(`${param.name}-${indentLevel}-${isResponseType}`) &&
                param.children &&
                param.children.length > 0 &&
                renderParams(param.children, indentLevel + 1, isResponseType)}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const serviceName = modelName?.split('.')[0];
  const model = modelName?.split('.')[1];

  return (
    <div
      className={
        'mt-2 mb-2 [&:not(:first-child)]:mt-6 border rounded-lg border-neutral-200 dark:border-neutral-800 p-4 bg-card hover:bg-card-hover transition-all ease-in duration-150 hover:shadow-md'
      }
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: FIXME: when touching this code, fix this lint issue */}
      <div
        className="mb-2 flex items-center justify-between cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <div className="bg-neutral-100 text-neutral-500 text-sm dark:bg-neutral-800/50 px-2 py-.9 rounded-md">
            {serviceName}
          </div>
          <span className="break-all">
            <span className="font-semibold">{model}</span>
          </span>
        </div>
        <div className="text-neutral-500">
          {isOpen ? <CaretDown weight="bold" size={20} /> : <CaretLeft weight="bold" size={20} />}
        </div>
      </div>
      {isOpen && (
        <div>
          {showCodeName && <p className="text-neutral-500/75 font-mono text-xs">({modelName})</p>}
          <p>{description}</p>
          <div className="border-t border-neutral-300 dark:border-neutral-700 w-full my-4" />
          <div>{mappedReqBodyParams && renderParams(mappedReqBodyParams)}</div>
        </div>
      )}
    </div>
  );
}

export default DataModel;
